import _ from 'lodash';
import utils from 'lib/utils';
import StateService from 'services/state-handler-services/state-service';

// TriggerService
//
// Condition matching for blocks that respond to events directly.
//
// This is the orchestration half of what the twine block used to do. A block
// declares its own `triggers` - an event, some conditions, and what to do - and
// calls this service to test an incoming event against them. That removes the
// twine node graph from pages that only ever used it to route an event to one
// block.
//
// It lives in a service because the condition grammar has to be the same
// everywhere. Content already depends on more than equality against the event
// payload: across the courses in flight, roughly a quarter of twine's
// conditions read a computed `$variable` and a sixth use a comparison operator.
// A per-block `matchesConditions` that only compared payload strings would
// leave those pages unable to migrate, and would leave every block that grew
// triggers with its own slightly different dialect.
//
// Conditions are written as an object, which is unambiguous and gives Forge a
// real form to render:
//
//   conditions:
//     id: m03-a05-p05Form_TacoNumbers   # equality against the event payload
//     correct: true
//     $expenseDifferenceW4: '>= 0'      # comparison against a state variable
//     model.attributes.state: complete  # lodash path into the payload
//
// The left-hand side names a state variable when prefixed with `$`, and the
// event payload otherwise (`#` may be used to say "payload" explicitly). The
// right-hand side is a literal, or a string beginning with an operator.
//
// Twine's comma-separated string form is still accepted so that a page can be
// migrated one block at a time.

// Operators, longest first so `===` is matched before `==`.
const OPERATORS = ['===', '!==', '==', '!=', '>=', '<=', '=', '>', '<'];

// The ones that put two values in order, as opposed to asking whether they are
// the same. Only these are refused a missing operand.
const ORDERING = ['>=', '<=', '>', '<'];

// Matches a right-hand side that leads with an operator, e.g. '>= 0'.
//
// A leading `<`, `>`, `=` or `!` is therefore reserved: a condition value of
// '<b>yes</b>' would read as "less than b>yes</b>". Nothing across the three
// courses in flight writes one, and every alternative rule considered was
// worse - requiring a space after the operator would reject '>=0', and
// inspecting the character after it would reject '==mid'. So this stays a
// documented restriction instead of a guess about intent. Quote or rephrase a
// value that has to start with one of those characters.
const OPERATOR_PREFIX = new RegExp(`^\\s*(${OPERATORS.join('|')})\\s*(.*)$`);

// Splits a legacy twine condition string entry into its three parts.
const LEGACY_CONDITION = /([^\s]+?)?\s*(===|!==|==|!=|>=|<=|=|>|<)\s*([^]+)?/;

const CONSTANTS = {
  true: true,
  false: false,
  null: null,
  undefined: undefined,
};

const TriggerService = {
  // Matching

  // Whether an event payload satisfies a trigger's conditions. No conditions
  // means the event alone is the trigger.
  matches: function (conditions, payload) {
    if (conditions === undefined || conditions === null || conditions === '') {
      return true;
    }

    return this.toComparisons(conditions).every(([lhs, operator, rhs]) => {
      // Bare strings on the left resolve against the payload even without a
      // `#`, which is how twine's `id==value` conditions are written.
      const lhsValue = this.resolveToken(lhs, payload, true);
      const rhsValue = this.resolveToken(rhs, payload);
      return this.compare(lhsValue, operator, rhsValue);
    });
  },

  // Normalize either condition form into [lhs, operator, rhs] triples.
  toComparisons: function (conditions) {
    if (typeof conditions === 'string') {
      return this.parseLegacyConditions(conditions);
    }

    if (Array.isArray(conditions)) {
      return conditions.reduce((comparisons, entry) => comparisons.concat(this.toComparisons(entry)), []);
    }

    return Object.entries(conditions).map(([key, value]) => {
      // A string value may lead with an operator: `$total: '>= 100'`.
      if (typeof value === 'string') {
        const match = value.match(OPERATOR_PREFIX);
        if (match) {
          return [key, match[1], match[2]];
        }
      }
      return [key, '==', value];
    });
  },

  // Twine wrote conditions as `name===value, $total>50`. Kept so a page can
  // move to triggers before its conditions are rewritten as objects.
  parseLegacyConditions: function (conditions) {
    return conditions
      .split(',')
      .map(condition => {
        const match = condition.match(LEGACY_CONDITION);
        if (match === null) {
          // A bare name means "this is truthy", e.g. `correct`.
          return [condition.trim(), '==', 'true'];
        }
        return [match[1] !== undefined ? match[1] : 'true', match[2], match[3] !== undefined ? match[3] : 'true'];
      })
      .filter(([lhs]) => lhs !== '');
  },

  // Resolve one side of a comparison to a value.
  //
  // `$name` reads state (and may carry a path, `$debts[0].remaining_balance`),
  // `#name` reads the payload, and a bare string reads the payload only when
  // `interpolateBareString` is set - otherwise it is its own literal value.
  resolveToken: function (token, payload, interpolateBareString = false) {
    if (typeof token !== 'string') {
      return token;
    }

    let value = token;

    if (token.charAt(0) === '$') {
      value = this.resolveStateToken(token);
    } else if (token.charAt(0) === '#') {
      value = _.get(payload, token.slice(1), null);
    } else if (interpolateBareString && !utils.isNumeric(token)) {
      value = _.get(payload, token, token);
    }

    // Coalesce the string spellings of these constants to the constants
    // themselves, so `correct: 'true'` matches a boolean payload value. Done
    // after lookup so a stored value of 'false' also coalesces.
    if (typeof value === 'string' && Object.prototype.hasOwnProperty.call(CONSTANTS, value)) {
      return CONSTANTS[value];
    }
    if (utils.isNumeric(value)) {
      return parseFloat(value);
    }
    return value;
  },

  // Read a `$variable`, going through StateService.get so a value an earlier
  // page persisted is resolved on demand.
  resolveStateToken: function (token) {
    const path = token.slice(1);
    const root = path.split(/[.[]/)[0];
    const value = StateService.get(root);

    if (path === root) {
      return value === undefined ? null : value;
    }
    return _.get({ [root]: value }, path, null);
  },

  compare: function (lhs, operator, rhs) {
    // Ordering something that isn't there. JS says `null >= 0` and `null < 5`,
    // so a condition meant as "once the total reaches zero" would fire on a
    // page the learner has not touched, while `> 0` on the same variable would
    // not. A variable with no value is "not yet" for every ordering operator.
    // Equality is left alone, so `$x: null` and `correct: false` still read the
    // way they look.
    if (ORDERING.includes(operator) && (lhs === undefined || lhs === null || rhs === undefined || rhs === null)) {
      return false;
    }

    switch (operator) {
      case '===':
        return lhs === rhs;
      case '!==':
        return lhs !== rhs;
      case '==':
      case '=':
        // Loose on purpose: a payload value and a YAML condition can be the
        // same value with different types.
        return lhs == rhs;
      case '!=':
        return lhs != rhs;
      case '<':
        return lhs < rhs;
      case '<=':
        return lhs <= rhs;
      case '>':
        return lhs > rhs;
      case '>=':
        return lhs >= rhs;
      default:
        console.error(`TriggerService: '${operator}' is not a valid operator`);
        return false;
    }
  },

  // Subscribing
  //
  // Wire a block's `triggers` config to a handler. Subscribing through the
  // view's own `subscribeEvent` means the listeners are torn down with the
  // block, which is the bug in the `Chaplin.mediator.subscribe` calls the
  // twine listeners were written with.
  //
  //   TriggerService.subscribe(this, this.model.get('triggers'), (trigger, payload) => {
  //     this.findAndPlay([trigger.anim_id], TriggerService.getValue(trigger, payload));
  //   });
  subscribe: function (view, triggers, handler) {
    if (!triggers) {
      return;
    }

    const list = Array.isArray(triggers) ? triggers : [triggers];
    list.forEach(trigger => {
      if (!trigger.event) {
        console.error('TriggerService: a trigger needs an event to listen for');
        return;
      }
      view.subscribeEvent(trigger.event, payload => {
        if (this.matches(trigger.conditions, payload)) {
          handler(trigger, payload);
        }
      });
    });
  },

  // The value a trigger passes on, for blocks that animate to a value
  // (a slider driving a lottie frame, say). `value_from` names a payload
  // property or a `$variable`; a literal `value` is used as-is.
  getValue: function (trigger, payload) {
    if (trigger.value !== undefined) {
      return trigger.value;
    }
    if (trigger.value_from === undefined) {
      // twine spelled this `valueToUse` and accepted either a literal number
      // or a payload property name.
      if (trigger.valueToUse === undefined) {
        return undefined;
      }
      return utils.isNumeric(trigger.valueToUse) ? trigger.valueToUse : _.get(payload, trigger.valueToUse);
    }
    return this.resolveToken(trigger.value_from, payload);
  },
};

export default TriggerService;
export { OPERATORS, ORDERING };
